import axios, { type AxiosError } from 'axios'
import axiosRetry, { isSafeRequestError } from 'axios-retry'
import catchUnauthorizedError from '@/utils/catchUnauthorizedError'
import { applyRequestContext } from '@/utils/applyRequestContext'
import { vaiErrorHandler } from '@/utils/vai/vaiErrorHandler'
import { markVaiAuthRedirect } from '@/utils/vai/notifyVaiError'
import { logInfra, sanitizeUrl } from '@/utils/logInfra'
import { HTTP_STATUS_UNAUTHORIZED } from '@/constants/AGENT_CHAT'

declare module 'axios' {
  interface AxiosRequestConfig {
    // Skip the vaiErrorHandler toast: the caller reports the failure itself.
    ignoreGlobalNotify?: boolean
    skipDefaultUnauthorizedBehaviour?: boolean
    ignoreGlobalAuthTokenSet?: boolean
  }
}

const RETRY_DELAY_MS = 2000
const ABORTED_REQUEST_CODE = 'ECONNABORTED'
const TIMEOUT_ERROR_CODES = [ABORTED_REQUEST_CODE, 'ETIMEDOUT']
const NETWORK_ERROR_CODE = 'ERR_NETWORK'
const SAFE_METHOD_FOR_ABORT_RETRY = 'get'

// Client for the vAI service (VITE_AI_BASE_URL and the agent API next to it).
// vAI is a side service with its own error bodies ({ message } / { detail }), so it
// does not go through httpClient's interceptor: a vAI failure never swaps the
// page for NotFound/ErrorPage, it only shows a toast (vaiErrorHandler). 401
// still logs the user out, as the token is shared with the main API.
const vaiClient = axios.create({
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
})

// Only reads are replayed (GET/HEAD/OPTIONS on a network error, 429 or 5xx).
// A write whose response was lost may already be applied: replaying a POST
// duplicates it, replaying a DELETE answers 404 for a delete that succeeded.
// axios-retry counts an aborted request as retryable, so cancellations are
// excluded explicitly ("drop previous" cancellables abort on every reload).
// A transport abort (ECONNABORTED from a keepalive / HTTP/2 GOAWAY race) is
// replayed for a GET, as httpClient does: axios-retry itself never retries it.
const shouldRetryVaiRequest = (error: AxiosError): boolean => {
  if (axios.isCancel(error)) {
    return false
  }

  const isAbortedRead = error.code === ABORTED_REQUEST_CODE
    && error.config?.method?.toLowerCase() === SAFE_METHOD_FOR_ABORT_RETRY

  return isAbortedRead || isSafeRequestError(error)
}

// Same infra events httpClient logs, so a vAI outage stays on the LogRocket timeline.
const logVaiInfra = (event: string, error: AxiosError): void => {
  logInfra(event, {
    url: sanitizeUrl(error.config?.url),
    method: String(error.config?.method ?? '').toUpperCase(),
    code: error.code ?? 'no-code',
    status: error.response?.status ?? 0,
    service: 'vai',
  })
}

axiosRetry(vaiClient, {
  retries: 1,
  retryCondition: shouldRetryVaiRequest,
  retryDelay: () => RETRY_DELAY_MS,
})

vaiClient.interceptors.request.use(applyRequestContext, (error) => Promise.reject(error))

// A retried request runs through this interceptor twice: once in the replayed
// request's own chain and once in the original one it resolves. The same error
// object reaches both, so it is marked to be reported only once.
const HANDLED_ERROR = Symbol('vaiClientHandled')

vaiClient.interceptors.response.use((response) => response, (error) => {
  // A throw from the request interceptor (e.g. no company slug) is not an axios error:
  // there is no request to report, so the caller gets it as is.
  if (!axios.isAxiosError(error) || axios.isCancel(error) || error[HANDLED_ERROR]) {
    return Promise.reject(error)
  }

  error[HANDLED_ERROR] = true

  if (TIMEOUT_ERROR_CODES.includes(String(error.code))) {
    logVaiInfra('[infra:client-timeout]', error)
  } else if (error.code === NETWORK_ERROR_CODE) {
    logVaiInfra('[infra:network-error]', error)
  }

  if (error?.response?.status === HTTP_STATUS_UNAUTHORIZED && !error.config?.skipDefaultUnauthorizedBehaviour) {
    catchUnauthorizedError()
    markVaiAuthRedirect(error)
    return Promise.reject(error)
  }

  vaiErrorHandler(error)

  return Promise.reject(error)
})

export default vaiClient
